import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, X } from 'lucide-react';
import {
  Campaign,
  BulkCampaignStatusFailure,
  useBulkUpdateCampaignStatus,
} from '@/api/campaigns';

interface CampaignBulkActionsBarProps {
  selectedCampaigns: Campaign[];
  campaignStatuses: Array<{
    id: number;
    name: string;
    color?: string | null;
    sortOrder?: number | null;
    isActive?: boolean | null;
  }>;
  onClear: () => void;
}

export function CampaignBulkActionsBar({
  selectedCampaigns,
  campaignStatuses,
  onClear,
}: CampaignBulkActionsBarProps) {
  const [targetStatusId, setTargetStatusId] = useState<string>('');
  const [failures, setFailures] = useState<BulkCampaignStatusFailure[] | null>(null);

  const bulkMutation = useBulkUpdateCampaignStatus();

  const sortedStatuses = useMemo(
    () =>
      campaignStatuses
        .filter((s) => s.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [campaignStatuses],
  );

  const selectedCount = selectedCampaigns.length;

  const allAlreadyAtTarget = useMemo(() => {
    if (!targetStatusId) return false;
    const id = Number.parseInt(targetStatusId, 10);
    if (Number.isNaN(id)) return false;
    return selectedCampaigns.every((c) => c.statusId === id);
  }, [selectedCampaigns, targetStatusId]);

  const isApplyDisabled =
    selectedCount === 0 ||
    !targetStatusId ||
    bulkMutation.isPending ||
    allAlreadyAtTarget;

  const handleApply = () => {
    const statusId = Number.parseInt(targetStatusId, 10);
    if (Number.isNaN(statusId)) return;

    const campaignIds = selectedCampaigns.map((c) => c.id);

    bulkMutation.mutate(
      { campaignIds, statusId },
      {
        onSuccess: (result) => {
          if (result.failedCount > 0) {
            setFailures(result.failed);
          } else {
            setFailures(null);
            onClear();
          }
          setTargetStatusId('');
        },
      },
    );
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900/50 dark:bg-blue-950/30"
        role="region"
        aria-label="Bulk campaign actions"
      >
        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
          {selectedCount} campaign{selectedCount === 1 ? '' : 's'} selected
        </span>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Set status to</span>
          <Select value={targetStatusId} onValueChange={setTargetStatusId}>
            <SelectTrigger className="h-9 w-[180px] bg-white dark:bg-background">
              <SelectValue placeholder="Choose status" />
            </SelectTrigger>
            <SelectContent>
              {sortedStatuses.map((status) => (
                <SelectItem key={status.id} value={status.id.toString()}>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: status.color || '#6B7280' }}
                    />
                    <span className="capitalize">{status.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          size="sm"
          onClick={handleApply}
          disabled={isApplyDisabled}
          className="h-9"
        >
          {bulkMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating…
            </>
          ) : (
            'Apply'
          )}
        </Button>

        {allAlreadyAtTarget && targetStatusId && !bulkMutation.isPending && (
          <span className="text-xs text-muted-foreground">
            All selected campaigns already have this status
          </span>
        )}

        <Button
          size="sm"
          variant="ghost"
          onClick={onClear}
          disabled={bulkMutation.isPending}
          className="ml-auto h-9"
        >
          <X className="mr-1 h-4 w-4" />
          Clear selection
        </Button>
      </div>

      <Dialog open={failures !== null} onOpenChange={(open) => { if (!open) setFailures(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk update partially complete</DialogTitle>
            <DialogDescription>
              Some campaigns could not be updated. Updated campaigns have been
              saved. Review the failures below and try again if needed.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-72 overflow-y-auto rounded-md border">
            <ul className="divide-y">
              {(failures ?? []).map((f) => (
                <li key={f.campaignId} className="p-3 text-sm">
                  <div className="font-medium">
                    {f.campaignName || `Campaign #${f.campaignId}`}
                  </div>
                  <div className="text-muted-foreground">{f.error}</div>
                  {f.code ? (
                    <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                      {f.code}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFailures(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
